import { useTranslation } from "react-i18next";

const statusConfig = {
  completed: { color: "text-emerald-400", border: "border-emerald-400/40", bg: "bg-emerald-400/10", key: "status_completed" },
  active: { color: "text-amber-400", border: "border-amber-400/40", bg: "bg-amber-400/10", key: "status_active" },
  upcoming: { color: "text-white/40", border: "border-white/20", bg: "bg-white/5", key: "status_upcoming" },
};

const phases = [
  {
    id: "01",
    nameKey: "phase1_name",
    yearKey: "phase1_year",
    status: "completed" as const,
    titleKey: "phase1_title",
    descKey: "phase1_desc",
    icon: "ri-checkbox-circle-fill",
    image: "https://public.readdy.ai/ai/img_res/4673ead83a115e591ad31daffa653223.jpg",
  },
  {
    id: "02",
    nameKey: "phase2_name",
    yearKey: "phase2_year",
    status: "active" as const,
    titleKey: "phase2_title",
    descKey: "phase2_desc",
    icon: "ri-loader-4-line",
    image: "https://readdy.ai/api/search-image?query=lunar%20lander%20spacecraft%20descending%20to%20moon%20surface%20with%20thruster%20fire%2C%20dramatic%20space%20scene%2C%20cinematic%20lighting%2C%20deep%20black%20space%20background%20with%20stars%2C%20photorealistic%20CGI%20render%2C%20golden%20sunlight%20on%20spacecraft%2C%20moon%20craters%20below&width=600&height=400&seq=phase002&orientation=landscape",
  },
  {
    id: "03",
    nameKey: "phase3_name",
    yearKey: "phase3_year",
    status: "upcoming" as const,
    titleKey: "phase3_title",
    descKey: "phase3_desc",
    icon: "ri-time-line",
    image: "https://readdy.ai/api/search-image?query=futuristic%20lunar%20base%20on%20moon%20surface%20with%20modular%20habitat%20domes%2C%20solar%20panels%2C%20astronauts%20in%20spacesuits%20working%20outside%2C%20earth%20visible%20in%20dark%20sky%2C%20cinematic%20NASA%20concept%20art%20style%2C%20dramatic%20lighting%2C%20photorealistic%20render&width=600&height=400&seq=phase003&orientation=landscape",
  },
  {
    id: "04",
    nameKey: "phase4_name",
    yearKey: "phase4_year",
    status: "upcoming" as const,
    titleKey: "phase4_title",
    descKey: "phase4_desc",
    icon: "ri-sun-line",
    image: "https://readdy.ai/api/search-image?query=massive%20space%20solar%20array%20satellite%20in%20orbit%20above%20earth%2C%20giant%20golden%20solar%20panels%20reflecting%20sunlight%2C%20deep%20black%20space%20background%2C%20earth%20curvature%20below%2C%20cinematic%20sci-fi%20concept%20art%2C%20ultra%20detailed%2C%20photorealistic%20CGI%2C%20dramatic%20lighting&width=600&height=400&seq=phase004&orientation=landscape",
  },
];

export default function TimelineSection() {
  const { t } = useTranslation();

  return (
    <section id="timeline" className="relative py-24 md:py-32 bg-[#050810]">
      <div className="absolute inset-0 opacity-5"
        style={{ backgroundImage: "radial-gradient(circle at 20% 50%, rgba(255,180,0,0.3) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,100,0,0.2) 0%, transparent 40%)" }}
      />
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="w-8 h-px bg-amber-400/60" />
            <span className="text-amber-400 text-xs tracking-[0.3em] uppercase" style={{ fontFamily: "'Orbitron', sans-serif" }}>{t("timeline_badge")}</span>
            <div className="w-8 h-px bg-amber-400/60" />
          </div>
          <h2 className="text-3xl md:text-5xl font-black text-white mb-4" style={{ fontFamily: "'Orbitron', sans-serif" }}>{t("timeline_title")}</h2>
          <p className="text-white/40 text-sm max-w-xl mx-auto">{t("timeline_subtitle")}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {phases.map((phase, index) => {
            const cfg = statusConfig[phase.status];
            return (
              <div key={phase.id} className={`relative group border ${cfg.border} rounded-lg overflow-hidden bg-white/[0.02] hover:bg-white/[0.04] transition-all duration-500`}>
                <div className="relative h-48 overflow-hidden">
                  <img src={phase.image} alt={t(phase.titleKey)} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050810] via-[#050810]/60 to-transparent" />
                  <div className="absolute top-4 left-4 text-5xl font-black opacity-20 text-white" style={{ fontFamily: "'Orbitron', sans-serif" }}>{phase.id}</div>
                  <div className={`absolute top-4 right-4 px-3 py-1 rounded-full text-xs tracking-widest uppercase ${cfg.bg} ${cfg.color} border ${cfg.border}`}>{t(cfg.key)}</div>
                </div>
                <div className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className={`text-xs tracking-[0.2em] uppercase mb-1 ${cfg.color}`} style={{ fontFamily: "'Orbitron', sans-serif" }}>{t(phase.nameKey)}</div>
                      <h3 className="text-white font-bold text-lg" style={{ fontFamily: "'Orbitron', sans-serif" }}>{t(phase.titleKey)}</h3>
                    </div>
                    <div className={`w-8 h-8 flex items-center justify-center rounded-full ${cfg.bg} border ${cfg.border} flex-shrink-0 ml-4`}>
                      <i className={`${phase.icon} ${cfg.color} text-sm`} />
                    </div>
                  </div>
                  <p className="text-white/50 text-sm leading-relaxed">{t(phase.descKey)}</p>
                  <div className={`mt-4 text-xs tracking-widest uppercase ${cfg.color} font-bold`} style={{ fontFamily: "'Orbitron', sans-serif" }}>{t(phase.yearKey)}</div>
                </div>
                <div className={`absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent ${index === 0 ? "via-emerald-400/60" : index === 1 ? "via-amber-400/60" : "via-white/20"} to-transparent`} />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
